import { defineStore } from 'pinia';
import { z } from 'zod';
import { computed, ref } from 'vue';
import { cloneDeep } from 'lodash-es';
import { StoreNames } from '../../../shared/store-names';
import { useUiStateAutoPersist, useUiStatesApi } from '../../ui-states';
import { multiSelectFilterValidationSchema, rangeFilterValidationSchema } from '../../filters';
import { useDataTableSettings } from '../../../composables';
import { LocationsColumns as Columns } from '../shared/locations-columns';
import type { ReplenishmentLocationsFilterState } from '../interfaces';

const UI_STATES_KEY = StoreNames.ReplenishmentLocationsPage;

export const useReplenishmentLocationsPageStore = defineStore(UI_STATES_KEY, () => {
  const defaultAppliedFilters: ReplenishmentLocationsFilterState = {
    region: ['in', []],
    city: ['in', []],
    weekly_inventory_needed_ratio: ['in_range', [null, null]],
  };

  const appliedFilters = ref<ReplenishmentLocationsFilterState>(cloneDeep(defaultAppliedFilters));

  const filtersValidationSchema = z.object({
    region: multiSelectFilterValidationSchema,
    city: multiSelectFilterValidationSchema,
    weekly_inventory_needed_ratio: rangeFilterValidationSchema,
  });

  const quickFilters = ref<string[]>([]);

  const {
    columnsVisibility,
    columnsWidth,
    columnsOrder,
    visibleColumns,
    resetColumnsSettings,
    applyPersistedState,
    columnsSettingsTouched,
    pinnedColumns,
    sorting,
    resetSorting,
    handleColumnResize,
    userColumnsWidth,
  } = useDataTableSettings({
    defaultColumnsVisibility: {
      [Columns.Name]: true,
      [Columns.ExternalId]: false,
      [Columns.LocationType]: false,
      [Columns.City]: false,
      [Columns.Sold]: true,
      [Columns.Coverage]: false,
      [Columns.Replenishment]: true,
      [Columns.ExpectedCoverage]: false,
      [Columns.AvgDailySales]: false,
      [Columns.SourceLocations]: false,
      [Columns.Region]: false,
      [Columns.TotalProducts]: true,
      [Columns.TotalSkus]: true,
      [Columns.ReplenishmentTime]: false,
      [Columns.Classifications]: false,
      [Columns.Brands]: false,
      [Columns.SoldDepleted]: false,
      [Columns.CoverageDepleted]: false,
      [Columns.AvgDailySalesDepleted]: false,
      [Columns.Constraints]: true,
      [Columns.WeeklyInventoryNeededRatio]: false,
    },
    defaultColumnsWidth: {
      [Columns.Name]: 224,
      [Columns.ExternalId]: 224,
      [Columns.LocationType]: 168,
      [Columns.Region]: 256,
      [Columns.City]: 192,
      [Columns.Brands]: 208,
      [Columns.Sold]: 168,
      [Columns.Coverage]: 168,
      [Columns.Replenishment]: 192,
      [Columns.ExpectedCoverage]: 200,
      [Columns.AvgDailySales]: 256,
      [Columns.SourceLocations]: 208,
      [Columns.TotalProducts]: 168,
      [Columns.TotalSkus]: 168,
      [Columns.ReplenishmentTime]: 264,
      [Columns.Classifications]: 208,
      [Columns.SoldDepleted]: 208,
      [Columns.AvgDailySalesDepleted]: 360,
      [Columns.CoverageDepleted]: 264,
      [Columns.WeeklyInventoryNeededRatio]: 264,
      [Columns.Constraints]: 264,
    },
    defaultColumnsOrder: [
      Columns.LocationType,
      Columns.City,
      Columns.Sold,
      Columns.Coverage,
      Columns.Replenishment,
      Columns.WeeklyInventoryNeededRatio,
      Columns.ExpectedCoverage,
      Columns.AvgDailySales,
      Columns.SourceLocations,
      Columns.Region,
      Columns.TotalProducts,
      Columns.TotalSkus,
      Columns.ReplenishmentTime,
      Columns.Classifications,
      Columns.Brands,
      Columns.SoldDepleted,
      Columns.CoverageDepleted,
      Columns.AvgDailySalesDepleted,
      Columns.Constraints,
    ],
    defaultPinnedColumns: [Columns.Name, Columns.ExternalId],
  });

  function reset() {
    resetColumnsSettings();
    resetSorting();
    appliedFilters.value = cloneDeep(defaultAppliedFilters);
  }

  const api = useUiStatesApi();

  const fetching = ref(false);
  const fetched = ref(false);

  let fetchingPromise: Promise<void> | null = null;

  async function fetch(force = false) {
    if (fetched.value && !force) {
      return fetchingPromise ?? Promise.resolve();
    }

    if (!fetchingPromise) {
      fetchingPromise = (async () => {
        fetching.value = true;

        const persistedState = await api
          .getUiStates({ key: UI_STATES_KEY })
          .then(({ data }) => data.data[0]);

        reset();

        // Apply persisted state

        if (persistedState) {
          applyPersistedState(persistedState.value);

          if (persistedState.value.appliedFilters) {
            try {
              appliedFilters.value = filtersValidationSchema.parse(
                persistedState.value.appliedFilters,
              );
            } catch (error) {
              // do nothing
            }
          }

          if (persistedState.value.quickFilters) {
            quickFilters.value = persistedState.value.quickFilters;
          }
        }

        fetching.value = false;
        fetched.value = true;
        fetchingPromise = null;
      })();
    }

    return fetchingPromise;
  }

  const persistingValue = computed(() => ({
    columnsVisibility: { ...columnsVisibility.value },
    columnsWidth: { ...userColumnsWidth.value },
    columnsOrder: [...columnsOrder.value],
    pinnedColumnsOrder: [...pinnedColumns.value],
    sorting: { ...sorting.value },
    appliedFilters: { ...appliedFilters.value },
    quickFilters: [...quickFilters.value],
  }));

  useUiStateAutoPersist({
    enabled: fetched,
    value: persistingValue,
    key: UI_STATES_KEY,
  });

  return {
    fetching,
    fetched,
    fetch,
    reset,
    resetColumnsSettings,
    columnsVisibility,
    columnsWidth,
    visibleColumns,
    columnsOrder,
    pinnedColumns,
    sorting,
    appliedFilters,
    columnsSettingsTouched,
    handleColumnResize,
    resetSorting,
    quickFilters,
  };
});
